import { z } from "zod";
import type { Path, UseFormReturn } from "react-hook-form";
import { EMAIL_RE, digitsLen } from "@/utils/validation";
import { req, opt, numAmount, digitsReq, digitsOpt, digitsRangeReq, zipField, lettersReq, lettersOpt, isSoleProprietor } from "@/features/onboarding/components/wizard";
import { FIELD_MAX } from "@/lib/fieldLimits";
import type { TKey, Translate } from "@/features/onboarding/i18n/translate";

export const STEP_KEYS = ["step.welcome", "step.legal", "step.business", "step.owner", "step.bank"] as const satisfies readonly TKey[];

export function makeSchema(t: Translate) {
  const M = FIELD_MAX.appWizard;
  const long = (label: string, max: number) => t("v.tooLong", { label, max });
  const required = (label: string) => t("v.required", { label });
  return z.object({
    legalName: req(required(t("legal.legalName"))).max(M.legalName, long(t("legal.legalName"), M.legalName)),
    dbaSameAsLegal: z.boolean(),
    dbaName: opt().max(M.dbaName, long(t("lbl.dbaName"), M.dbaName)),
    website: opt().max(M.website, long(t("legal.website"), M.website)),
    ownershipType: req(t("v.ownershipType")).max(M.ownershipType, long(t("legal.ownershipType"), M.ownershipType)),
    businessStartDate: req(required(t("legal.startDate"))).max(M.businessStartDate, long(t("legal.startDate"), M.businessStartDate)),
    mcc: digitsOpt(4, M.mcc, t("lbl.mcc"), t),
    addressLine1: req(required(t("legal.street"))).max(M.addressLine1, long(t("legal.street"), M.addressLine1)),
    addressLine2: opt().max(M.addressLine2, long(t("lbl.addressLine2"), M.addressLine2)),
    city: req(required(t("legal.city"))).max(M.city, long(t("legal.city"), M.city)),
    state: req(required(t("legal.state"))).max(M.state, long(t("legal.state"), M.state)),
    zip: zipField(true, M.zip, t("legal.zip"), t),
    dbaAddressLine1: opt().max(M.dbaAddressLine1, long(t("lbl.dbaStreet"), M.dbaAddressLine1)),
    dbaCity: opt().max(M.dbaCity, long(t("lbl.dbaCity"), M.dbaCity)),
    dbaState: opt().max(M.dbaState, long(t("lbl.dbaState"), M.dbaState)),
    dbaZip: zipField(false, M.dbaZip, t("lbl.dbaZip"), t),
    contactName: opt().max(M.contactName, long(t("lbl.contactName"), M.contactName)),
    email: z.string().trim().min(1, t("v.emailRequired")).regex(EMAIL_RE, t("v.emailInvalid")).max(M.email, long(t("legal.email"), M.email)),
    phone: digitsReq(10, M.phone, t("legal.phone"), t),
    averageMonthlyVolume: numAmount(t),
    averageTransactionAmount: numAmount(t),
    maxTransactionAmount: numAmount(t),
    productDescription: opt().max(M.productDescription, long(t("lbl.productDescription"), M.productDescription)),
    ownerCitizenship: z.enum(["us", "non-us"]),
    ownerFirstName: lettersReq(M.ownerFirstName, t("owner.firstName"), t),
    ownerMiddleName: lettersOpt(M.ownerMiddleName, t("lbl.middleName"), t),
    ownerLastName: lettersReq(M.ownerLastName, t("owner.lastName"), t),
    ownerTitle: req(t("v.title")).max(M.ownerTitle, long(t("owner.jobTitle"), M.ownerTitle)),
    ownerDateOfBirth: req(required(t("owner.dob"))).max(M.ownerDateOfBirth, long(t("owner.dob"), M.ownerDateOfBirth)),
    ownerSsn: opt().max(25, long(t("lbl.idNumber"), 25)),
    ownerPassportNumber: opt(),
    ownerPassportCountry: opt(),
    ownerPhone: digitsReq(10, M.ownerPhone, t("lbl.phoneNumber"), t),
    ownerHomeAddressLine1: req(required(t("legal.street"))).max(M.ownerHomeAddressLine1, long(t("legal.street"), M.ownerHomeAddressLine1)),
    ownerHomeCity: req(required(t("legal.city"))).max(M.ownerHomeCity, long(t("legal.city"), M.ownerHomeCity)),
    ownerHomeState: req(required(t("legal.state"))).max(M.ownerHomeState, long(t("legal.state"), M.ownerHomeState)),
    ownerHomeZip: zipField(true, M.ownerHomeZip, t("legal.zip"), t),
    ein: digitsOpt(9, M.ein, t("lbl.ein"), t),
    ssn: digitsOpt(9, M.ssn, t("lbl.ssn"), t),
    bankName: opt().max(M.bankName, long(t("bank.bankName"), M.bankName)),
    depositAccountType: opt().max(M.depositAccountType, long(t("bank.accountType"), M.depositAccountType)),
    bankRoutingNumber: digitsReq(9, M.bankRoutingNumber, t("bank.routing"), t),
    bankAccountNumber: digitsRangeReq(1, 17, M.bankAccountNumber, t("bank.account"), t),
    separateWithdrawAccount: z.boolean(),
    withdrawBankName: opt().max(M.withdrawBankName, long(t("bank.bankName"), M.withdrawBankName)),
    withdrawAccountType: opt().max(M.withdrawAccountType, long(t("bank.accountType"), M.withdrawAccountType)),
    withdrawRoutingNumber: opt(),
    withdrawAccountNumber: opt(),
    specialNotes: opt(),
  }).superRefine((v, ctx) => {
    if (isSoleProprietor(v.ownershipType)) {
      if (!v.ssn.trim()) ctx.addIssue({ code: "custom", path: ["ssn"], message: t("v.ssnSole") });
    } else if (!v.ein.trim()) {
      ctx.addIssue({ code: "custom", path: ["ein"], message: t("v.einRequired") });
    }
    if (v.ownerCitizenship === "us") {
      if (!v.ownerSsn.trim()) {
        ctx.addIssue({ code: "custom", path: ["ownerSsn"], message: t("v.ssnRequired") });
      } else if (!digitsLen(v.ownerSsn, 9)) {
        ctx.addIssue({ code: "custom", path: ["ownerSsn"], message: t("v.digits", { label: t("lbl.ssn"), n: 9 }) });
      }
    } else {
      const passport = v.ownerPassportNumber.replace(/\s/g, "");
      if (!passport) {
        ctx.addIssue({ code: "custom", path: ["ownerPassportNumber"], message: t("v.passportRequired") });
      } else if (!/^[A-Za-z0-9]{5,20}$/.test(passport)) {
        ctx.addIssue({ code: "custom", path: ["ownerPassportNumber"], message: t("v.passportInvalid") });
      }
      if (!v.ownerPassportCountry.trim()) {
        ctx.addIssue({ code: "custom", path: ["ownerPassportCountry"], message: t("v.passportCountryRequired") });
      }
    }
    if (v.separateWithdrawAccount) {
      const routing = t("bank.withdrawRouting");
      const account = t("bank.withdrawAccount");
      if (!v.withdrawRoutingNumber.trim()) {
        ctx.addIssue({ code: "custom", path: ["withdrawRoutingNumber"], message: required(routing) });
      } else if (!digitsLen(v.withdrawRoutingNumber, 9)) {
        ctx.addIssue({ code: "custom", path: ["withdrawRoutingNumber"], message: t("v.digits", { label: routing, n: 9 }) });
      }
      const accountDigits = v.withdrawAccountNumber.replace(/\D/g, "").length;
      if (!v.withdrawAccountNumber.trim()) {
        ctx.addIssue({ code: "custom", path: ["withdrawAccountNumber"], message: required(account) });
      } else if (accountDigits < 1 || accountDigits > 17) {
        ctx.addIssue({ code: "custom", path: ["withdrawAccountNumber"], message: t("v.digitsRange", { label: account, min: 1, max: 17 }) });
      }
    }
    if (!v.dbaSameAsLegal && !v.dbaName.trim()) {
      ctx.addIssue({ code: "custom", path: ["dbaName"], message: t("v.dbaRequired", { option: t("legal.dbaYes") }) });
    }
  });
}

export type FormState = z.infer<ReturnType<typeof makeSchema>>;

export interface StepProps {
  form: UseFormReturn<FormState>;
}

export const STEP_FIELDS: Partial<Record<number, Path<FormState>[]>> = {
  1: ["legalName", "email", "phone", "addressLine1", "zip", "state", "city", "ownershipType", "businessStartDate", "ssn", "ein", "website", "dbaSameAsLegal", "dbaName", "dbaAddressLine1", "dbaCity", "dbaState", "dbaZip"],
  2: ["averageMonthlyVolume", "averageTransactionAmount", "maxTransactionAmount", "productDescription"],
  3: ["ownerFirstName", "ownerMiddleName", "ownerLastName", "ownerDateOfBirth", "ownerTitle", "ownerCitizenship", "ownerSsn", "ownerPassportNumber", "ownerPassportCountry", "ownerPhone", "ownerHomeAddressLine1", "ownerHomeZip", "ownerHomeState", "ownerHomeCity"],
  4: ["bankName", "depositAccountType", "bankRoutingNumber", "bankAccountNumber", "separateWithdrawAccount", "withdrawBankName", "withdrawAccountType", "withdrawRoutingNumber", "withdrawAccountNumber", "specialNotes"],
};

export const EMPTY_FORM: FormState = {
  legalName: "", dbaSameAsLegal: true, dbaName: "", website: "",
  ownershipType: "", businessStartDate: "", mcc: "",
  addressLine1: "", addressLine2: "", city: "", state: "", zip: "",
  dbaAddressLine1: "", dbaCity: "", dbaState: "", dbaZip: "",
  contactName: "", email: "", phone: "",
  averageMonthlyVolume: "", averageTransactionAmount: "", maxTransactionAmount: "",
  productDescription: "",
  ownerCitizenship: "us",
  ownerFirstName: "", ownerMiddleName: "", ownerLastName: "", ownerTitle: "", ownerDateOfBirth: "",
  ownerSsn: "", ownerPassportNumber: "", ownerPassportCountry: "", ownerPhone: "",
  ownerHomeAddressLine1: "", ownerHomeCity: "", ownerHomeState: "", ownerHomeZip: "",
  ein: "", ssn: "", bankName: "", depositAccountType: "",
  bankRoutingNumber: "", bankAccountNumber: "",
  separateWithdrawAccount: false, withdrawBankName: "", withdrawAccountType: "",
  withdrawRoutingNumber: "", withdrawAccountNumber: "",
  specialNotes: "",
};
